import React, { forwardRef, memo, useEffect, useId, useRef, useState } from 'react'
import ReactPlayer, { ReactPlayerProps } from 'react-player/lazy'
import { useCurrentMediaPlaying, useForkedRef } from '@evertel/hooks'
import { FloatingMenu, MenuHeader, MenuItem } from '..'
import { Icon } from './Icon'

function formatDuration(ms: number): string {
    const totalSeconds = Math.floor(ms / 1000)
    const hours = Math.floor(totalSeconds / 3600)
    const minutes = Math.floor((totalSeconds % 3600) / 60)
    const seconds = totalSeconds % 60

    const pad = (n: number) => n.toString().padStart(2, '0')

    if (hours > 0) {
        return `${hours}:${pad(minutes)}:${pad(seconds)}`
    }
    return `${minutes}:${pad(seconds)}`
}

export interface VideoProps extends ReactPlayerProps {
    url: string
    fileName?: string
    controls?: boolean
    width?: number | string
    height?: number | string
    maxWidth?: number | string
    maxHeight?: number | string
    fallbackWidth?: number | string
    rounded?: boolean
    thumbnailUrl?: string
    duration?: number
    asThumbnail?: boolean
    fit?: 'contain' | 'cover' | 'fill' | 'scale' | 'none' | 'scale-down' | 'inherit' | 'initial' | 'revert' | 'revert-layer' | 'unset'
    enableContextMenu?: boolean
    onDownload: () => void
}

const Video: React.FC<any> = memo(forwardRef<ReactPlayer, VideoProps>(({
    url,
    fileName = '',
    controls = true,
    width,
    height,
    maxWidth,
    maxHeight,
    minWidth,
    fallbackWidth = '600px',
    rounded,
    thumbnailUrl,
    duration,
    asThumbnail = false,
    enableContextMenu = false,
    onDownload,
    ...otherProps
}, ref) => {

    const { currentMediaPlaying, setCurrentMediaPlaying } = useCurrentMediaPlaying()
    const player = useRef(null)
    const forkedRef = useForkedRef(ref, player)

    const contextMenuRef = useRef<HTMLDivElement>(null)

    const [isLoading, setIsLoading] = useState(true)
    const [playing, setPlay] = useState(false)
    const [activated, setActivated] = useState(false)

    useEffect(() => {
        if (!currentMediaPlaying || currentMediaPlaying === url || !playing) {
            return
        }
        setPlay(false)
    }, [currentMediaPlaying])

    const onReady = () => {
        setIsLoading(false)
    }

    const onPlay = () => {
        // notify context to stop other players
        setCurrentMediaPlaying(url)
        setPlay(true)
    }

    const onPause = () => {
        setPlay(false)
    }

    if (!width && !height) {
        width = fallbackWidth
        height = undefined
    }

    const handleContextMenu = (e: React.MouseEvent<HTMLElement>) => {
        if (!enableContextMenu) {
            e.preventDefault()
            e.stopPropagation()
        }
    }

    const handleThumbnailClick = () => {
        setActivated(true)
        setPlay(true)
        setCurrentMediaPlaying(url)
    }

    const htmlId = useId()

    // Static thumbnail mode: display-only, never plays video (used in gallery strips, search lists, etc.)
    if (asThumbnail) {
        return (
            <div
                id={htmlId}
                className="video-as-thumbnail"
            >
                {thumbnailUrl ? (
                    <img
                        src={thumbnailUrl}
                        alt={fileName || 'Video thumbnail'}
                        className="video-as-thumbnail-img"
                        style={{
                            width: width,
                            height: height,
                            borderRadius: rounded ? '0.4rem' : 0
                        }}
                    />
                ) : (
                    <video
                        src={`${url}#t=0.1`}
                        preload="metadata"
                        muted
                        className="video-as-thumbnail-img"
                        style={{
                            width: width,
                            height: height,
                            borderRadius: rounded ? '0.4rem' : 0,
                            pointerEvents: 'none'
                        }}
                        crossOrigin="use-credentials"
                    />
                )}
                <div className="video-as-thumbnail-indicator">
                    <Icon
                        name="play"
                        type="light"
                        color="white"
                        size={12}
                    />
                </div>
                {(duration != null && duration > 0) && (
                    <div className="video-duration-badge">
                        {formatDuration(duration)}
                    </div>
                )}
            </div>
        )
    }

    // Thumbnail preview mode: show static JPEG with play overlay
    if (thumbnailUrl && !activated) {
        return (
            <div
                className="video-thumbnail-container"
                style={{
                    width: width,
                    height: height,
                    maxWidth: maxWidth || '100%',
                    maxHeight: maxHeight || '100%',
                    borderRadius: rounded ? '0.4rem' : 0
                }}
                role="button"
                tabIndex={0}
                aria-label={`Play video${fileName ? `: ${fileName}` : ''}`}
                onClick={handleThumbnailClick}
                onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault()
                        handleThumbnailClick()
                    }
                }}
            >
                <img
                    src={thumbnailUrl}
                    alt={fileName || 'Video thumbnail'}
                    className="video-thumbnail-img"
                    style={{
                        borderRadius: rounded ? '0.4rem' : 0
                    }}
                />
                <div className="video-play-overlay">
                    <div className="video-play-button">
                        <Icon
                            name="play"
                            type="light"
                            color="white"
                            size={30}
                        />
                    </div>
                </div>
                {(duration != null && duration > 0) && (
                    <div className="video-duration-badge">
                        {formatDuration(duration)}
                    </div>
                )}
            </div>
        )
    }

    // Active video player mode (or legacy mode when no thumbnail available)
    return (
        <div id={htmlId} ref={contextMenuRef} style={{ display: 'contents'}}>
            <ReactPlayer
                ref={forkedRef}
                url={url}
                playing={playing}
                controls={controls && !isLoading}
                onReady={onReady}
                onPlay={onPlay}
                onPause={onPause}
                width={width}
                height={height}
                style={{
                    maxWidth: maxWidth || '100%',
                    maxHeight: maxHeight || '100%',
                    minWidth: minWidth || '200px'
                }}
                config={{
                    file: {
                        attributes: {
                            style: {
                                //items here won't ever update with params
                                backgroundColor: 'black',
                                width: '100%',
                                height: '100%',
                                maxWidth: '100%',
                                maxHeight: '100%',
                                borderRadius: (rounded) ? '0.4rem' : 0
                            },
                            crossOrigin: 'use-credentials',
                            controlsList: 'nodownload'
                        }
                    }
                }}
                {...(!enableContextMenu && { onContextMenu: handleContextMenu })}
                {...otherProps}
            />
            {enableContextMenu &&
                <FloatingMenu contextTarget={contextMenuRef} attachId={htmlId}>
                    <MenuHeader label={fileName || 'Media Element'} />
                    <MenuItem
                        label="Download"
                        icon={{ name: 'cloud-arrow-down' }}
                        onClick={() => onDownload()}
                    />
                </FloatingMenu>
            }
        </div>
    )
}
))

export { Video }
